import cn from 'classnames';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';

import { Alert, AuthLoader } from '@infologistics/frontend-libraries';

import EmailForm from './components/EmailForm/EmailForm';
import PhoneForm from './components/PhoneForm/PhoneForm';
import SSOAuth from './components/SSOAuth';

import { ISigninFormProps as IProps } from './types';

import styles from '../AuthNew.module.scss';
import { BrowserStorage, RouteName, SigninFormMode } from '@const/consts';
import { createPortal } from 'react-dom';
import { displayBasicNotification } from '@app/utils/utils';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@app/store/types/commonTypes';
import tokensService from '@app/services/tokensService';

const SigninForm: FC<IProps> = (props) => {
  const { history } = props;

  const [mode, setMode] = useState<SigninFormMode>(SigninFormMode.EMAIL);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);

  const { oguid: userOguid, orgs } = useSelector((state: IApplicationState) => state.user.profile);
  const userOrgsCount = orgs.length;
  const { t } = useTranslation(['auth']);

  const modalRoot = document.getElementById('modal-root');

  const handleIsSendingChange = (value: boolean): void => {
    setIsSending(value);
  };

  const handleServerErrorChange = (value?: string): void => {
    setServerError(value);
  };

  useEffect(() => {
    const authorized = tokensService.checkAccessToken();
    const federationToken = tokensService.getTokenFromCookies(BrowserStorage.TOKEN_FEDERATION, userOguid);

    if (authorized && federationToken !== null) history.push(RouteName.DEFAULT);

    // If when trying to log in
    // it turns out that the user does not have available organizations
    if (userOguid && !userOrgsCount) {
      displayBasicNotification({
        content: t('auth:orgErrorContent'),
        title: t('auth:orgErrorTitle'),
        type: 'error',
      });
    }
  }, []);

  const getForm = (): ReactElement => {
    const forms = {
      [SigninFormMode.EMAIL]: (
        <EmailForm
          {...props}
          isSending={isSending}
          onSetMode={setMode}
          onIsSendingChange={handleIsSendingChange}
          onServerErrorChange={handleServerErrorChange}
        />
      ),
      [SigninFormMode.PHONE]: (
        <PhoneForm {...props} isSending={isSending} onSetMode={setMode} onServerErrorChange={handleServerErrorChange} />
      ),
      [SigninFormMode.SSO]: <SSOAuth onSetMode={setMode} />,
    };

    return forms[mode];
  };

  return (
    <>
      <div className={cn(styles.alert_wrapper, 'mb-2')}>
        {serverError && <Alert classes={cn(styles.alert, styles.alert_danger)} title={serverError} theme='danger' />}
      </div>

      {getForm()}

      {/* temporarily unused
        <p className={cn(styles.registration_wrapper, 'd-flex', 'justify-content-between')}>
          {t('auth:noAccount')}
          <Link to={RouteName.SIGN_UP} className={cn(styles.sign_up_link, 'relative')}>
            {t('auth:signUp')}
          </Link>
        </p>
      */}
      {isSending && modalRoot ? createPortal(<AuthLoader />, modalRoot) : null}
    </>
  );
};

export default withTranslation()(withRouter(SigninForm));
